import React, { Fragment, FunctionComponent } from 'react';
import { useTranslation } from '@rbx/intl';
import { Grid, Typography } from '@rbx/ui';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import ContentTable from '@modules/ui/shared/components/ContentTable';
import Chips from '@modules/ui/shared/components/Chips';
import { capitalizeFirstLetter } from '@modules/article/utils/capitalize';
import useObjectReferenceUtils from '../../../context/hooks/useObjectReferenceUtils';
import type { OpenCloudAPIParameter } from '../../../utils/oasTypes';
import OpenCloudType from '../../sharedUI/components/OpenCloudType';

interface ParameterMainTableProps {
  parameters: OpenCloudAPIParameter[];
  colonBeforeType?: boolean;
}

const defaultParameterTag = 'query';

const ParameterMainTable: FunctionComponent<React.PropsWithChildren<ParameterMainTableProps>> = ({
  parameters,
  colonBeforeType = true,
}) => {
  const { translate } = useTranslation();
  const { dereference, isObjectReference } = useObjectReferenceUtils();

  // FIXME: reduce code duplication in https://jira.rbx.com/browse/CRK-995
  return (
    <ContentTable
      aria-label={translate('Label.ParameterTable')}
      data={parameters}
      renderer={(row) => {
        const { required, description, name, schema } = row as OpenCloudAPIParameter;
        const tag = (row as OpenCloudAPIParameter).in;
        let tags =
          tag && tag.toLowerCase() !== defaultParameterTag ? [capitalizeFirstLetter(tag)] : [];
        tags = required ? [...tags, translate('Label.Required')] : tags;
        let schemaDescription;
        if (isObjectReference(schema)) {
          const objectReference = dereference(schema);

          if (objectReference === null) {
            throw new Error('Unknown object reference!');
          }

          const { content } = objectReference;
          schemaDescription = content.description;
        } else {
          schemaDescription = schema?.description;
        }
        const parameterDescription = description ?? schemaDescription;
        return {
          name: (
            <Fragment>
              <Typography variant='body2'>{`${name}${colonBeforeType ? ':' : ''} `}</Typography>
              <OpenCloudType schema={schema} />
            </Fragment>
          ),
          tags: (
            <Grid item marginTop={1}>
              <Chips labels={tags} />
            </Grid>
          ),
          summary: parameterDescription && (
            <StandardMarkdownNoMargin>{parameterDescription}</StandardMarkdownNoMargin>
          ),
        };
      }}
    />
  );
};

export default ParameterMainTable;
